import React, { FC, useState } from 'react';
import { Alert, Button, Collapse, Modal, Skeleton, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import TextField from './fields/TextField';
import { FlashIcon, RemoveIcon } from '~/img';
import CopyButton from './CopyButton';
import { useGetApiKeyInfo } from '~/hooks/terminals';
import { useMutation, useQueryClient } from 'react-query';
import { issueApiKey, revokeApiKey } from '~/api/terminals';
import moment from 'moment';
import { terminals } from '~/routes';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const { Panel } = Collapse;

interface IntegrationProps {
  terminalId: string;
  className?: string;
}

const Integration: FC<IntegrationProps> = (props) => {
  const { className, terminalId } = props;
  const [t] = useTranslation('terminals');
  const queryClient = useQueryClient();

  const [isAlertOn, setIsAlertOn] = useState<boolean>(false);
  const [integrationData, setIntegrationData] = useState({
    tokenPrefix: '',
    createdAt: '',
    value: '',
  });

  const { data: apiKeyData, isLoading: apiKeyDataLoading } = useGetApiKeyInfo(terminalId);

  const tokenPrefix = integrationData?.tokenPrefix || apiKeyData?.tokenPrefix;
  const createdAt =
    (integrationData?.createdAt && moment(integrationData?.createdAt).format('LL LT')) ||
    (apiKeyData?.createdAt && moment(apiKeyData?.createdAt).format('LL LT'));
  const isRevokeAvailable =
    (integrationData?.tokenPrefix && integrationData?.createdAt) ||
    (apiKeyData?.tokenPrefix && apiKeyData?.createdAt);

  const mutationToIssue = useMutation(issueApiKey, {
    onSuccess: (data) => {
      setIntegrationData({
        tokenPrefix: data?.tokenPrefix,
        createdAt: data?.createdAt,
        value: data?.value || '',
      });
      queryClient.invalidateQueries(['apiKeyInfo', terminalId]);
      queryClient.invalidateQueries(terminals);
      setIsAlertOn(true);
    },
  });

  const mutationToRevoke = useMutation(revokeApiKey, {
    onSuccess: () => {
      setIntegrationData({
        tokenPrefix: '',
        createdAt: '',
        value: '',
      });
      queryClient.invalidateQueries(['apiKeyInfo', terminalId]);
      queryClient.invalidateQueries(terminals);
      setIsAlertOn(false);
    },
  });

  const handleRevokeApiKey = () => {
    Modal.confirm({
      title: t('warning'),
      icon: <ExclamationCircleOutlined />,
      content: t('revokeWarningContent'),
      okText: t('confirm'),
      cancelText: t('cancel'),
      onOk: () => {
        mutationToRevoke.mutateAsync(terminalId);
      },
    });
  };
  const handleIssueApiKey = () => {
    Modal.confirm({
      title: t('warning'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          {isRevokeAvailable && <p>{t('reissueWarningContent')}</p>}
          <p>{isRevokeAvailable ? t('reissueWarningContent1') : t('issueWarningContent')}</p>
          <ul>
            <li>{isRevokeAvailable ? t('reissueWarningContent2') : t('issueWarningContent1')}</li>
            <li>{isRevokeAvailable ? t('reissueWarningContent3') : t('issueWarningContent2')}</li>
            {isRevokeAvailable && <li>{t('reissueWarningContent4')}</li>}
          </ul>
          <p>{isRevokeAvailable ? t('reissueWarningContent5') : t('issueWarningContent3')}</p>
        </>
      ),
      okText: t('confirm'),
      cancelText: t('cancel'),
      onOk: () => {
        mutationToIssue.mutateAsync(terminalId);
      },
    });
  };
  const handleAlertClose = () => {
    setIsAlertOn(false);
  };
  return (
    <Collapse ghost className={className} defaultActiveKey={['1']}>
      <Panel header={t('integration')} key="1">
        <Space direction="vertical" size="middle">
          <Typography.Title level={5}>{t('apiKey')}</Typography.Title>
          <TextField label={t('tokenPrefix')}>
            {apiKeyDataLoading || mutationToIssue.isLoading ? (
              <Skeleton.Button active size="small" style={{ height: 16, minWidth: 150 }} />
            ) : (
              tokenPrefix
            )}
          </TextField>
          <TextField label={t('dateOfCreation')}>
            {apiKeyDataLoading || mutationToIssue.isLoading ? (
              <Skeleton.Button active size="small" style={{ height: 16, minWidth: 150 }} />
            ) : (
              createdAt
            )}
          </TextField>
          <Space direction="horizontal" size="small">
            <Button
              icon={<FlashIcon />}
              size="middle"
              type="default"
              className="button"
              onClick={handleIssueApiKey}
              loading={mutationToIssue.isLoading}
            >
              {t('issueApiKey')}
            </Button>
            {isRevokeAvailable && (
              <Button
                icon={<RemoveIcon />}
                size="middle"
                danger
                type="text"
                className="button"
                onClick={handleRevokeApiKey}
                loading={mutationToRevoke.isLoading}
              >
                {t('revokeApiKey')}
              </Button>
            )}
          </Space>
          {isAlertOn && (
            <Alert
              type="success"
              closable
              onClose={handleAlertClose}
              description={
                <Space direction="vertical" size="middle">
                  <Typography.Text strong>{t('yourApiKeyWarning')}</Typography.Text>
                  <Typography.Text style={{ maxWidth: '260px', wordBreak: 'break-word' }}>
                    {mutationToIssue.isLoading ? (
                      <Skeleton.Button active size="small" style={{ height: 16, minWidth: 150 }} />
                    ) : (
                      integrationData?.value
                    )}
                  </Typography.Text>
                  <CopyButton text={integrationData?.value} />
                </Space>
              }
            />
          )}
        </Space>
      </Panel>
    </Collapse>
  );
};

export default styled(Integration)`
  border: 1px solid #d9d9d9;
  border-radius: 4px;
`;
